import BigNumber from "bignumber.js";
import Long from "long";
import {
  OrderIntent,
  OrderType,
  BenchmarkOrderType,
  OrderState,
} from "shared/cubiq-model-multi-asset";
import { riskLogger } from "shared/cubiq-services/lookup/model/impl/bmrisk/SpotRiskModel";
import { BmarkOrderData } from "shared/cubiq-services/model/benchmarks/BmarkOrderData.model";
import { BmarkRiskData } from "shared/cubiq-services/model/benchmarks/BmarkRiskData.model";
import { BENCHMARK_TERMINAL_STATE } from "shared/cubiq-services/model/benchmarks/BmarkTerminalState.const";

/**
 * Class describing the Risk for a trader Group
 */
export class SpotRiskTGNet {
  /** Map of risk ID to base Amt */
  riskBaseAmtIdx: Map<string, BigNumber> = new Map();
  orderFillAmtIdx: Map<number, BigNumber> = new Map();
  strDbgPfx: string;

  public netBaseAmt: BigNumber = new BigNumber(0);
  public netFillAmt: BigNumber = new BigNumber(0);

  /** True if any orders active - if false, all orders are unfilled and terminal  */
  public hasActiveOrders: boolean = false;

  constructor(
    public tgId: Long,
    public tgName: string,
  ) {
    this.strDbgPfx = "[" + this.tgName + (this.tgId ? ":" + this.tgId + "] " : "] ");
  }

  public getNumOrders(): number {
    return this.riskBaseAmtIdx.size;
  }

  applyRisk(risk: BmarkRiskData, order: BmarkOrderData, applyFill: boolean = true): boolean {
    let ret: boolean = false;
    if (!risk && !order) {
      riskLogger.trace(this.strDbgPfx + " : attempt to applyRisk with neither an order nor a fill");
      return ret;
    }

    let tradingBase: boolean = true;
    let intent: OrderIntent = null;
    let baseAmt: BigNumber = null;
    let fillAmt: BigNumber = null;
    let contributesFillAmtInsteadOfOpenAmt: boolean = false;

    if (risk) {
      tradingBase = risk.fixedCcy == risk.baseCcy;
      intent = this.intent(risk, tradingBase);
      baseAmt = this.baseAmt(risk, tradingBase);
    }

    if (order) {
      fillAmt = risk.useNPV ? risk.npvFilledBaseAmount : risk.filledBaseAmount;

      let isPositionOrder = false;
      if (
        null != order.fixingDetails &&
        order.orderType === OrderType.BENCHMARK &&
        BenchmarkOrderType.PositionOrder == order.fixingDetails.bmOrderType
      ) {
        isPositionOrder = true;
      }

      contributesFillAmtInsteadOfOpenAmt =
        (order?.orderType === OrderType.BMALGO || isPositionOrder) &&
        BENCHMARK_TERMINAL_STATE.has(order.orderState);

      if (
        (OrderState.FILL_EXPIRED == order.orderState ||
          order.orderState == OrderState.FILL_ERROR) &&
        isPositionOrder
      ) {
        contributesFillAmtInsteadOfOpenAmt = true;
      }
    }

    // KD : Total is the order amt of active order and filled amount of terminal.
    if (contributesFillAmtInsteadOfOpenAmt) {
      // if its terminal (or a Fill expired BMHO)
      if (null != risk && null != intent && null != fillAmt /* && !baseAmt.isZero() */) {
        riskLogger.debug(
          this.strDbgPfx +
            " : terminal : order id [" +
            order.orderId +
            "] : fillAmt [" +
            fillAmt +
            "]",
        );
        ret = this.addRisk(risk.riskId, intent, fillAmt.abs()); // FILL AMT
      }
    } else {
      // if it's active
      if (null != risk && null != intent && null != baseAmt /* && !baseAmt.isZero()*/) {
        this.hasActiveOrders = true;
        ret = this.addRisk(risk.riskId, intent, baseAmt); // BASE AMT
      }
    }

    if (
      applyFill &&
      null != order &&
      null != intent &&
      null != fillAmt /* && !fillAmt.isZero() */
    ) {
      this.addFill(order.orderId, intent, fillAmt.abs());
    }

    return ret;
  }

  removeRisk(risk: BmarkRiskData, order: BmarkOrderData): boolean {
    let ret: boolean = false;
    if (!risk && !order) {
      riskLogger.trace(
        this.strDbgPfx + " : attempt to removeRisk with neither an order nor a fill",
      );
      return ret;
    }

    let tradingBase: boolean = true;
    let intent: OrderIntent = null;
    let baseAmt: BigNumber = null;

    if (risk) {
      tradingBase = risk.fixedCcy == risk.baseCcy;
      intent = this.intent(risk, tradingBase);
      baseAmt = this.baseAmt(risk, tradingBase);
    }

    if (null != risk && null != intent && null != baseAmt) {
      ret = this.resetRisk(risk.riskId, intent);
    }

    if (null != order && null != intent) {
      this.resetFill(order.orderId, intent);
    }

    return ret;
  }

  addRisk(riskId: string, intent: OrderIntent, baseAmt: BigNumber): boolean {
    let ret: boolean = false;
    let knownOrderBase: BigNumber = this.riskBaseAmtIdx.get(riskId);
    if (!knownOrderBase || !knownOrderBase.isEqualTo(baseAmt)) {
      this.netBaseAmt =
        intent == OrderIntent.BUY ? this.netBaseAmt.plus(baseAmt) : this.netBaseAmt.minus(baseAmt);

      this.riskBaseAmtIdx.set(riskId, baseAmt);

      riskLogger.debug(
        this.strDbgPfx +
          " : addRisk id[" +
          riskId +
          "] +Base[" +
          (intent == OrderIntent.BUY ? "B" : "S") +
          ":" +
          baseAmt +
          "] => netBase[" +
          this.netBaseAmt +
          "] ",
      );

      ret = true;
    } else {
      if (!knownOrderBase) {
        riskLogger.debug(
          this.strDbgPfx + " : addRisk ? id[" + riskId + "] not found in riskBaseAmtIdx - NOP",
        );
      } else {
        riskLogger.debug(
          this.strDbgPfx +
            " : addRisk ? id[" +
            riskId +
            "] baseAmt Same:[" +
            knownOrderBase +
            "] - NOP",
        );
      }
    }

    return ret;
  }

  resetRisk(riskId: string, intent: OrderIntent): boolean {
    let ret: boolean = false;
    let currentRiskAmt: BigNumber = this.riskBaseAmtIdx.get(riskId);
    if (null != currentRiskAmt) {
      // For SWaps this'll be a NOP
      this.netBaseAmt =
        intent == OrderIntent.BUY
          ? this.netBaseAmt.minus(currentRiskAmt)
          : this.netBaseAmt.plus(currentRiskAmt);
      this.riskBaseAmtIdx.delete(riskId);
      if (this.riskBaseAmtIdx.size == 0) {
        this.hasActiveOrders = false;
      }

      riskLogger.debug(
        this.strDbgPfx +
          " : resetRisk id [" +
          riskId +
          "] -Base[" +
          (intent == OrderIntent.BUY ? "B" : "S") +
          ":" +
          currentRiskAmt +
          "] => netBase[" +
          this.netBaseAmt +
          "] ",
      );

      ret = true;
    } else {
      riskLogger.debug(
        this.strDbgPfx + " : resetRisk ? id[" + riskId + "] not found in riskBaseAmtIdx - NOP",
      );
    }
    return ret;
  }

  addFill(orderId: Long, intent: OrderIntent, fillAmt: BigNumber) {
    let knownOrderFill: BigNumber = this.orderFillAmtIdx.get(orderId.toNumber());
    if (knownOrderFill == null || !knownOrderFill.isEqualTo(fillAmt)) {
      this.orderFillAmtIdx.set(orderId.toNumber(), fillAmt);

      this.netFillAmt =
        intent == OrderIntent.BUY ? this.netFillAmt.plus(fillAmt) : this.netFillAmt.minus(fillAmt);

      riskLogger.debug(
        this.strDbgPfx +
          " :  addFill ? id[" +
          orderId +
          "] Fill[" +
          fillAmt +
          "] => netFill[" +
          this.netFillAmt +
          "] ",
      );
    } else {
      if (!knownOrderFill) {
        riskLogger.debug(
          this.strDbgPfx + " :  addFill ? id[" + orderId + "] not found in orderFillAmtIdx - NOP",
        );
      } else {
        riskLogger.debug(
          this.strDbgPfx +
            " :  addFill ? id[" +
            orderId +
            "] fillAmt Same:[" +
            knownOrderFill +
            "] - NOP",
        );
      }
    }
  }

  resetFill(orderId: Long, intent: OrderIntent) {
    let prevFillAmtForOrder: BigNumber = this.orderFillAmtIdx.get(orderId.toNumber());
    if (null != prevFillAmtForOrder) {
      this.netFillAmt =
        intent == OrderIntent.BUY
          ? this.netFillAmt.minus(prevFillAmtForOrder)
          : this.netFillAmt.plus(prevFillAmtForOrder);

      this.orderFillAmtIdx.delete(orderId.toNumber());

      riskLogger.debug(
        this.strDbgPfx + " :  resetFill ? id[" + orderId + "] => netFill[" + this.netFillAmt + "] ",
      );
    }
  }

  private intent(risk: BmarkRiskData, tradingBase: boolean): OrderIntent {
    let intent: OrderIntent = null;

    if (risk.useNPV) {
      intent = tradingBase
        ? risk.npvIntent
        : risk.npvIntent == OrderIntent.BUY
          ? OrderIntent.SELL
          : OrderIntent.BUY;
    } else {
      intent = tradingBase
        ? risk.netIntent
        : risk.netIntent == OrderIntent.BUY
          ? OrderIntent.SELL
          : OrderIntent.BUY;
    }

    return intent;
  }

  private baseAmt(risk: BmarkRiskData, tradingBase: boolean): BigNumber {
    let baseAmt: BigNumber = null; // NB Is this always base Amt?

    if (risk.useNPV) {
      baseAmt = tradingBase ? risk.npvRiskAmount : risk.npvCounterAmount;
    } else {
      baseAmt = tradingBase ? risk.riskAmount : risk.counterAmount;
    }

    riskLogger.debug(
      this.strDbgPfx +
        " : [Order " +
        risk.orderRef +
        "] Trading base [" +
        tradingBase +
        "] BaseAmt [" +
        baseAmt +
        "]" +
        (tradingBase ? "" : " OrderAmt [" + risk.riskAmount + "]"),
    );

    return baseAmt;
  }

  public hasRisk(riskId: string): boolean {
    return this.riskBaseAmtIdx != null && this.riskBaseAmtIdx.has(riskId);
  }

  public toString(): string {
    let _toString: string =
      "" +
      this.strDbgPfx +
      "[Num: " +
      this.getNumOrders() +
      "]" +
      "[Base:" +
      this.netBaseAmt +
      "]" +
      "[fill:" +
      this.netFillAmt +
      "]";

    return _toString;
  }
}
